import _extends from "@babel/runtime-corejs3/helpers/esm/extends";
import React from "react";
import PropTypes from "prop-types";
import classesPropType from "@xo-union/classes-prop-type";
import { AnimatedElement } from "../Animation.js";
import LoadingContainer from "../LoadingContainer/index.js";
import CtaContainer from "../CtaContainer/index.js";
import { useButtonClasses } from "../ThemeContext.js";
import { getClassName } from "../../utilities/index.js";

/**
 * Render a non interactive button. This is ideal when rendering a button inside
 * of another interactive element (such as an interactive Card)
 */
const NonInteractiveButton = props => {
  const {
    active,
    as: Type,
    block,
    children,
    classes: classesProp,
    className,
    color,
    disabled,
    iconName,
    isLoading,
    labelDirection,
    loadingText,
    size,
    ...passThroughProps
  } = props;
  const classes = useButtonClasses(classesProp);
  return /*#__PURE__*/React.createElement(Type, _extends({
    className: getClassName(props, false, classes)
  }, passThroughProps), /*#__PURE__*/React.createElement(AnimatedElement, {
    classes: classesProp
  }), isLoading ? /*#__PURE__*/React.createElement(LoadingContainer, {
    classes: classesProp,
    loadingText: loadingText
  }) : /*#__PURE__*/React.createElement(CtaContainer, {
    classes: classesProp,
    color: color,
    iconName: iconName,
    labelDirection: labelDirection,
    size: size
  }, children));
};
process.env.NODE_ENV !== "production" ? NonInteractiveButton.propTypes = {
  /**
   * Class dependencies
   */
  classes: classesPropType(),
  /**
   * Use disabled styles. Ideally you should use the `disabled` prop on the parent `LinkContainer`
   * or `ButtonContainer`
   */
  disabled: PropTypes.bool,
  /**
   * Component or element to render
   */
  as: PropTypes.elementType,
  /**
   * Breakpoint where button changes to block
   * */
  block: PropTypes.oneOf(['xs', 'sm', 'md', 'lg', 'xl', true]),
  /**
   * Color class
   * */
  color: PropTypes.oneOf(['primary', 'secondary', 'tertiary', 'quaternary', 'primary-alternative', 'secondary-alternative', 'tertiary-alternative', 'quaternary-alternative']),
  /**
   * Size class
   * */
  size: PropTypes.oneOf(['sm', 'md', 'lg']),
  /**
   * The button's content
   */
  children: PropTypes.node,
  /**
   * Click handler
   */
  onClick: PropTypes.func,
  /**
   * Add additional classes
   */
  className: PropTypes.string,
  /**
   * Force active state.  Ideally you should use the `disabled` prop on the parent `LinkContainer`
   * or `ButtonContainer`
   */
  active: PropTypes.bool,
  /**
   * Text to display during the `isLoading` state
   */
  loadingText: PropTypes.string,
  /**
   * Loading state. Use this when the result of a button click is an async request
   */
  isLoading: PropTypes.bool,
  /**
   * Icon to place next to CTA text.
   */
  iconName: PropTypes.string,
  /**
   * Determines whether the CTA text will be on the left or right side of the icon.
   */
  labelDirection: PropTypes.oneOf(['left', 'right'])
} : void 0;
NonInteractiveButton.defaultProps = {
  as: 'span',
  size: 'lg',
  color: 'primary',
  isLoading: false,
  loadingText: 'Loading'
};
export default NonInteractiveButton;